import { Add, Cancel, Edit } from '@mui/icons-material';
import { Box, FormControl, FormLabel, Grid, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import { BLUE_GRAY_50, BLUE_GRAY_500, GRAY_200, GRAY_900 } from '../../constants/Colors';
import { useAppDispatch } from '../../redux_store/reduxStore';
import { showErrorSnackbar } from '../../redux_store/slices/snackbarSlice';
import { t } from 'i18next';
import { readAndCompressImage } from 'browser-image-resizer';

export interface BakeasyImgSelectProps {
  imgUrl?: any;
  onImageSelect?: any;
  id: string;
  name: string;
  value: any;
  label: string;
  placeholder?: string;
  headerText?: string;
  disabled?: boolean;
  footerText?: string;
  uploadState?: string;
  isEdit?: boolean;
  onChange?: (file: File | string | null) => void;
  onClick?: () => void;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024;
export const BakeasyImgSelector: React.FC<BakeasyImgSelectProps> = (
  props: BakeasyImgSelectProps
) => {
  const { register, reset } = useForm();
  const [uploadState, setUploadState] = React.useState(props?.uploadState);
  const [image, setImage] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');
  const dispatch = useAppDispatch();

  const readFileAsDataURL = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const loadImage = (dataUrl: any) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });
  };

  const handleFileChange = async (file: any) => {
    if (file) {
      try {
        const dataUrl = await readFileAsDataURL(file);
        const img: any = await loadImage(dataUrl);

        // const isValidImage =
        //   (img.width === 1280 && img.height === 720) || (img.width === 1920 && img.height === 1080);

        const newImgFile: any = await scaleImage(img, file);
        return newImgFile;
      } catch (error) {
        return false;
      }
    }
  };

  const scaleImage = async (img: any, file: File) => {
    const width = img.width;
    const height = img.height;
    const td = 480;
    const sd = 640;
    const hd = 1280;
    let newWidth = width;
    let newHeight = height;

    if (width > height) {
      // Landscape (Horizontal)
      newWidth = sd;
      newHeight = (newWidth / width) * height;
    } else if (height > width) {
      // Portrait (Vertical)
      newHeight = sd;
      newWidth = (newHeight / height) * width;
    } else {
      // Square
      newWidth = td;
      newHeight = td;
    }

    // Resize image using browser-image-resizer
    const resizedImage: Blob = await readAndCompressImage(file, {
      maxWidth: newWidth,
      maxHeight: newHeight,
      mimeType: 'image/png'
    });

    let d = createFileFromBlob(resizedImage, file?.name);
    return d;
  };

  // Function to convert the Blob into a File object
  const createFileFromBlob = (blob: Blob, fileName: string): File => {
    // Create a File object from the Blob
    const file = new File([blob], fileName, { type: blob.type });
    return file;
  };

  const validateAndProcessFile = async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      dispatch(showErrorSnackbar({ message: t('FILE_SIZE_MSG') }));
      return false;
    }
    const newFile: any = await handleFileChange(file);
    return newFile;
  };

  const handleUploadClick = async (event: any) => {
    const file = event.target.files?.[0];
    if (file) {
      let d = await validateAndProcessFile(file);
      if (d === false) {
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(d);
      reader.onloadend = function() {
        if (reader.result) {
          setImage(reader.result as string);
          setUploadState('uploaded');
          props?.onChange && props?.onChange(d);
        }
      };
    }
  };

  const handleResetClick = () => {
    if (props?.value) {
      setUploadState('initFromProps');
    } else {
      setUploadState('initial');
    }
    setUploadState(props?.uploadState);
    setImage('');
    setError('');
    reset({ [props?.name]: null });
    props?.onChange && props?.onChange(null);
  };

  const handleImageChange = async (event: any) => {
    const file = event.target.files?.[0];
    if (file) {
      let d = await validateAndProcessFile(file);
      if (d === false) {
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(d);
      reader.onloadend = function() {
        if (reader.result) {
          setImage(reader.result as string);
          setUploadState('uploaded');
          props?.onChange && props?.onChange(d);
        }
      };
    }
  };

  // Function to create a file from a Blob
  const downloadFile = (blob: Blob, fileName: string) => {
    // Create a temporary URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName; // Set the filename for the file
    link.click(); // Simulate a click to start the download

    // Clean up the URL object after the download
    URL.revokeObjectURL(url);
  };

  return (
    <FormControl>
      <>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <FormLabel sx={{ fontWeight: 'bolder', marginBottom: 0.5 }}>{props.label}</FormLabel>
          {/* {props?.headerText && (uploadState === 'initial' || !props?.disabled) && (
            <FormHelperText
              sx={{
                margin: 0,
                paddingTop: 0.5,
                paddingLeft: 1,
                color: 'BLUE_GRAY_500',
                fontSize: '0.75rem'
              }}
            >
              {props?.headerText}
            </FormHelperText>
          )} */}
        </div>
      </>

      <Box
        sx={{
          //maxWidth: '256px',
          // maxHeight: '144px',
          // width: '100%',
          // height: '100%',
          width: '256px',
          //maxHeight: '144px',
          height: '144px',
          border: uploadState === 'initial' ? '0.75px dashed' : '',
          borderColor: GRAY_900,
          borderRadius: 2.75,
          position: 'relative'
        }}
      >
        <Grid container justifyContent="center" alignItems="center" style={{ height: '100%' }}>
          <input
            accept="image/jpeg,image/png,image/jpg"
            id={props?.id}
            {...register(props?.name, { required: true })}
            type="file"
            onChange={handleUploadClick}
            style={{ display: 'none' }}
          />

          {uploadState === 'initial' && (
            <label htmlFor={props.id}>
              <IconButton component="span">
                <Add />
              </IconButton>
            </label>
          )}

          {uploadState === 'initFromProps' && (
            <Grid container style={{ height: '100%' }}>
              <div style={{ height: '100%', position: 'relative' }}>
                <img
                  src={props?.value}
                  alt={props?.label}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'scale-down',
                    borderRadius: '12px',
                    cursor: props?.onClick ? 'pointer' : 'default'
                  }}
                  //crossOrigin="anonymous"
                  onClick={props?.onClick}
                />
                {!props?.disabled && (
                  <IconButton
                    component="label"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      bgcolor: GRAY_200,
                      opacity: 1,
                      boxShadow: 1,
                      padding: 0.5,
                      '&:hover': { bgcolor: BLUE_GRAY_50 }
                    }}
                  >
                    <Edit fontSize="small" />
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                  </IconButton>
                )}
              </div>
            </Grid>
          )}

          {uploadState === 'uploaded' && (
            <Grid container style={{ height: '100%' }}>
              <div style={{ height: '100%', position: 'relative' }}>
                <img
                  src={image}
                  alt={props?.label}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain', //chnage to cover
                    borderRadius: '12px'
                  }}
                />
                <IconButton
                  onClick={handleResetClick}
                  style={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    background: 'rgba(255, 255, 255, 1)',
                    padding: 0
                  }}
                  disabled={props.disabled}
                >
                  <Cancel />
                </IconButton>
              </div>
            </Grid>
          )}
        </Grid>
      </Box>

      {props?.footerText && (uploadState === 'initial' || !props?.disabled) && (
        <ImageUploadMessage />
        // <FormHelperText
        //   id="outlined-weight-helper-text"
        //   sx={{
        //     '&.MuiFormHelperText-root': {
        //       margin: 0,
        //       paddingLeft: 0.5,
        //       color: BLUE_GRAY_500,
        //       fontSize: '0.75rem'
        //     }
        //   }}
        // >
        //   {props?.footerText}
        // </FormHelperText>
      )}
    </FormControl>
  );
};

export const BakeasyImgSelectorView: React.FC<BakeasyImgSelectProps> = (
  props: BakeasyImgSelectProps
) => {
  const [uploadState, setUploadState] = React.useState(props?.uploadState);

  return (
    <FormControl>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <FormLabel sx={{ fontWeight: 'bolder', marginBottom: 0.5 }}>{props.label}</FormLabel>
      </div>

      <Box
        sx={{
          width: '256px',
          height: '144px',
          // width: '100%',
          // height: '100%',
          //border: '0.75px dashed',
          borderColor: GRAY_900,
          borderRadius: 2.75,
          position: 'relative'
        }}
      >
        <Grid container style={{ height: '100%' }}>
          {uploadState === 'initFromProps' && (
            <div style={{ height: '100%', position: 'relative' }}>
              <img
                src={props?.value}
                alt={props?.label}
                style={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                  cursor: props?.onClick ? 'pointer' : 'default',
                  borderRadius: '12px'
                }}
                onClick={props?.onClick}
                //crossOrigin="anonymous"
              />
            </div>
          )}
        </Grid>
      </Box>
    </FormControl>
  );
};

export const ImageUploadMessage = () => {
  return (
    <Typography style={{ display: 'inline', fontSize: '0.75rem', color: BLUE_GRAY_500 }}>
      {t('IMAGE_UPLOAD_MESSAGE')}
    </Typography>
  );
};
